'use client';

import { useRandomInterval } from '@hooks/useRandomInterval';
import classNames from 'classnames';
import { useId, useRef } from 'react';

interface GoalMountainGrapicProps {
  className?: string;
  animated?: boolean;
  entered?: boolean;
  active?: boolean;
  withSheen?: boolean;
  sheenMinMs?: number;
  sheenMaxMs?: number;
}

function GoalMountainGrapic({
  className,
  animated = false,
  entered = false,
  active = true,
  withSheen = false,
  sheenMinMs = 10000,
  sheenMaxMs = 20000
}: GoalMountainGrapicProps) {
  const clipId = useId();
  const gradId = useId();
  const sheenRef = useRef<SVGRectElement>(null);

  const fireSheen = () => {
    const el = sheenRef.current;
    if (!el) return;
    el.classList.remove('go');
    el.getBBox(); // force reflow so the animation restarts on each fire
    el.classList.add('go');
  };

  useRandomInterval(sheenMinMs, sheenMaxMs, fireSheen, {
    enabled: animated && withSheen && entered && active
  });

  return (
    <svg
      width='100%'
      height='100%'
      viewBox='0 0 143 156'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={classNames(
        className,
        animated && 'goal-mtn',
        animated && entered && 'is-entered',
        animated && !active && 'is-paused'
      )}
    >
      {animated && withSheen && (
        <defs>
          <clipPath id={clipId}>
            <path d='M70.2787 149.67C104.649 149.67 132.647 130.25 133.944 105.971H6.61523C7.91224 130.252 35.9238 149.67 70.2805 149.67H70.2787Z' />
          </clipPath>
          <linearGradient id={gradId} x1='0' y1='0' x2='1' y2='0'>
            <stop offset='0%' stopColor='#ffffff' stopOpacity='0' />
            <stop offset='50%' stopColor='#ffffff' stopOpacity='0.55' />
            <stop offset='100%' stopColor='#ffffff' stopOpacity='0' />
          </linearGradient>
        </defs>
      )}
      <path
        className='s-bowl'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M70.2787 149.67C104.649 149.67 132.647 130.25 133.944 105.971H6.61523C7.91224 130.252 35.9238 149.67 70.2805 149.67H70.2787Z'
        fill='#3A3A3A'
      />
      <path
        className='s-dot d1'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M2.98965 118.572C4.46495 118.572 5.66582 117.4 5.66582 115.96C5.66582 114.52 4.4667 113.348 2.98965 113.348C1.5126 113.348 0.313477 114.518 0.313477 115.96C0.313477 117.401 1.51435 118.572 2.98965 118.572Z'
        fill='#E5E5EA'
      />
      <path
        className='s-dot d2'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M137.857 100.383C140.29 100.383 142.27 98.4049 142.27 95.9696C142.27 93.5344 140.293 91.5566 137.857 91.5566C135.422 91.5566 133.444 93.5373 133.444 95.9696C133.444 98.402 135.425 100.383 137.857 100.383Z'
        fill='#3A3A3A'
      />
      <path
        className='s-dot d3'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M41.4052 155.858C43.4706 155.858 45.1853 154.144 45.1853 152.076C45.1853 150.007 43.4706 148.293 41.4052 148.293C39.3397 148.293 37.6201 150.007 37.6201 152.076C37.6201 154.144 39.3348 155.858 41.4052 155.858Z'
        fill='#625FDB'
      />
      <path
        className='s-dot d4'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M128.524 139.232C129.999 139.232 131.2 138.06 131.2 136.62C131.2 135.18 130.001 134.008 128.524 134.008C127.047 134.008 125.848 135.179 125.848 136.62C125.848 138.062 127.047 139.232 128.524 139.232Z'
        fill='#FFBB53'
      />
      <path
        className='s-bowl'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M119.258 133.274C107.577 143.279 89.9779 149.662 70.2833 149.662C50.5887 149.662 32.9899 143.279 21.3081 133.274C13.7778 125.921 22.6873 114.758 32.2173 120.237C46.147 128.251 79.7171 147.683 119.26 133.274H119.258Z'
        fill='#595959'
      />
      <path
        className='s-dot d5'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M40.7557 13.3844C42.5202 13.3844 43.9244 11.9811 43.9244 10.2174C43.9244 8.45371 42.5202 7.08008 40.7557 7.08008C38.9912 7.08008 37.6201 8.48338 37.6201 10.2174C37.6201 11.9514 39.0243 13.3844 40.7557 13.3844Z'
        fill='#625FDB'
      />
      <path
        className='s-dot d6'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M135.966 56.2546C138.033 56.2546 139.749 54.5718 139.749 52.4517C139.749 50.3316 138.033 48.6895 135.966 48.6895C133.899 48.6895 132.184 50.3723 132.184 52.4517C132.184 54.5311 133.899 56.2546 135.966 56.2546Z'
        fill='#E5E5EA'
      />
      <path
        className='s-dot d7'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M8.61948 58.7761C10.6863 58.7761 12.4021 57.0618 12.4021 54.9935C12.4021 52.9252 10.6863 51.2109 8.61948 51.2109C6.55267 51.2109 4.83691 52.9252 4.83691 54.9935C4.83691 57.0618 6.55267 58.7761 8.61948 58.7761Z'
        fill='#3A3A3A'
      />
      <path
        className='s-dot d8'
        fillRule='evenodd'
        clipRule='evenodd'
        d='M92.4667 9.60139C94.8995 9.60139 96.8797 7.62366 96.8797 5.18839C96.8797 2.75312 94.9024 0.775391 92.4667 0.775391C90.0311 0.775391 88.0537 2.75312 88.0537 5.18839C88.0537 7.62366 90.0311 9.60139 92.4667 9.60139Z'
        fill='#FFBB53'
      />
      {animated && withSheen && (
        <g clipPath={`url(#${clipId})`}>
          <rect
            ref={sheenRef}
            className='sheen-rect'
            x='0'
            y='102'
            width='34'
            height='52'
            fill={`url(#${gradId})`}
          />
        </g>
      )}
      <g className='s-mountains'>
        <path
          d='M77.4165 63.9394C75.743 63.9394 74.3848 62.5825 74.3848 60.9076V46.0885C74.3848 44.4149 75.7416 43.0566 77.4165 43.0566C79.0915 43.0566 80.4483 44.4135 80.4483 46.0885V60.9076C80.4483 62.5811 79.0915 63.9394 77.4165 63.9394Z'
          fill='#595959'
        />
        <path
          d='M82.9974 111.14L55.7159 68.7015C54.6003 66.9641 51.7312 66.9641 50.6155 68.7015L23.334 111.14C22.7341 112.072 22.6919 113.258 23.2236 114.23C23.7553 115.203 24.7766 115.809 25.885 115.809H80.448C81.5564 115.809 82.5776 115.203 83.1093 114.23C83.641 113.258 83.5974 112.071 82.9989 111.14H82.9974Z'
          fill='#9D90E1'
        />
        <path
          d='M31.2744 115.808H80.4479C81.5563 115.808 82.5776 115.203 83.1093 114.229C83.6409 113.257 83.5974 112.07 82.9989 111.139L58.6518 73.2656L31.2744 115.808Z'
          fill='#9D90E1'
        />
        <path
          d='M113.311 111.139L79.9674 59.2699C78.8517 57.5324 75.9826 57.5324 74.867 59.2699L38.4844 115.809H110.76C111.868 115.809 112.89 115.204 113.421 114.23C113.953 113.258 113.909 112.072 113.311 111.14V111.139Z'
          fill='#FAF7F5'
        />
        <path
          d='M80.929 62.2997C81.0874 62.0528 81.3402 61.922 81.5595 61.7448L79.9674 59.2679C78.8517 57.5305 75.9826 57.5305 74.867 59.2679L38.4844 115.807H46.4974L80.929 62.2997Z'
          fill='#E2DDFC'
        />
        <path
          d='M77.4165 82.1264C82.7304 82.1264 87.5809 80.1492 91.3071 76.9097L79.9659 59.2679C78.8503 57.5305 75.9812 57.5305 74.8656 59.2679L63.5244 76.9097C67.2506 80.1492 72.1011 82.1264 77.415 82.1264H77.4165Z'
          fill='#9D90E1'
        />
        <path
          d='M80.9296 62.2997C81.0879 62.0528 81.3407 61.922 81.56 61.7448L79.9679 59.2679C78.8522 57.5305 75.9832 57.5305 74.8675 59.2679L63.5264 76.9097C65.2187 78.3813 67.1508 79.5739 69.2456 80.4572L80.9296 62.2997Z'
          fill='#9D90E1'
        />
      </g>
      <path
        className='s-flag'
        d='M101.585 35.7408C103.926 36.8078 103.926 40.1333 101.585 41.2004L78.591 51.6812C76.6044 52.5867 74.3467 51.1346 74.3467 48.9514L74.3467 27.9897C74.3467 25.8065 76.6044 24.3544 78.591 25.2599L101.585 35.7408Z'
        fill='#FFBB53'
      />
    </svg>
  );
}

export default GoalMountainGrapic;
